/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext, IImageData, Image } from '@msdyn365-commerce/core';
import { ISearchData, ISearchProductViewProps } from '@msdyn365-commerce-modules/search';
import { INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import { ISearchProps } from '../../definition-extensions/search.ext.props.autogenerated';
import { SearchSuggestion } from '@msdyn365-commerce/retail-proxy';
//import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Render Products Suggestion result heading.
 * @param productSuggestionsHeading -The products suggestion result heading.
 * @returns The JSX Element.
 */
const renderProductSuggestionsTitle = (productSuggestionsHeading: string, searchText: string): JSX.Element => {
    const productAutoSuggestionHeading = productSuggestionsHeading ? productSuggestionsHeading : 'Products';

    return (
        <div className='msc-autoSuggest__productResults-title'>
            <a href={goToSearch(searchText)}>{productAutoSuggestionHeading}</a>
        </div>
    );
};

/**
 * Render Products Suggestion result heading.
 * @param props -The props.
 * @returns The JSX Element.
 */
const renderProductNotFoundResultsImage = (props: ISearchProps<ISearchData>): JSX.Element | null => {
    const noSearchResultImage: IImageData | undefined = props.config.noSearchResultImage;
    if (!noSearchResultImage || !noSearchResultImage.src) {
        return null;
    }
    return (
        <Image
            altText={noSearchResultImage.altText}
            className='msc-autoSuggest__productResults-no-results-image-img'
            gridSettings={props.context.request.gridSettings!}
            src={noSearchResultImage.src}
        />
    );
};

/**
 * Render Products No result section.
 * @param props -The props.
 * @returns The JSX Element.
 */
const renderProductNotFoundResults = (props: ISearchProps<ISearchData>): JSX.Element => {
    return (
        <Node className='msc-autoSuggest__productResults-no-results-content'>
            <div className='msc-autoSuggest__productResults-no-results-image'>{renderProductNotFoundResultsImage(props)}</div>
            <div className='msc-autoSuggest__productResults-no-results-content-title'>{props.resources.noResultContentHeadingText}</div>
            <div className='msc-autoSuggest__productResults-no-results-content-paragraph'>
                {props.resources.noResultContentParagraphText}
            </div>
        </Node>
    );
};
/**
 * Render Product suggestion result.
 * @param productSuggest -The product suggest Node props.
 * @param ulProduct -The List Node props.
 * @param props -The props.
 * @param productSuggestions -The Product suggestion result.
 * @param isLoadingAutoSuggest - The autosuggestion loading flag.
 * @param isLoadingNode - The autosuggestion loading node.
 * @returns The JSX Element.
 */
export const ProductSuggestionsComponent = (
    productSuggest: INodeProps,
    ulProduct: INodeProps,
    props: ISearchProps<ISearchData>,
    productSuggestions?: ISearchProductViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
): JSX.Element => {
    const liProduct: INodeProps = {
        tag: 'li',
        className: 'msc-no-result-content'
    };

    return productSuggestions ? (
        <Node {...productSuggest}>
            {renderProductSuggestionsTitle(props.resources.productSuggestionHeading, props.data.searchState.searchText!)}
            <Node {...ulProduct}>
                {isLoadingAutoSuggest && isLoadingNode}
                {!isLoadingAutoSuggest &&
                    productSuggestions.items.map((item, index) => {
                        return (
                            <Node {...item.LiProduct} key={item.id ?? index}>
                                <Node {...item.AProduct} href={goToSearch(props.data.searchState.searchText!)}>
                                    {item.thumbnail}
                                    {item.text}
                                    {item.price}
                                </Node>
                            </Node>
                        );
                    })}
            </Node>
        </Node>
    ) : (
        <Node {...productSuggest}>
            {renderProductSuggestionsTitle(props.resources.productSuggestionHeading, props.data.searchState.searchText!)}
            <Node {...ulProduct}>
                <Node {...liProduct}>{renderProductNotFoundResults(props)}</Node>
            </Node>
        </Node>
    );
};

export const SimpleProductSuggestionsComponent = (
    productSuggest: INodeProps,
    ulProduct: INodeProps,
    suggestions: SearchSuggestion[],
    ctx: IActionContext,
    searchText: string
): JSX.Element => {
    return suggestions && suggestions.length > 0 ? (
        <Node {...productSuggest}>
            {renderProductSuggestionsTitle('Customer Part', searchText)}
            <Node {...ulProduct}>
                {suggestions.map(suggestion => (
                    <li key={suggestion.Id} className='msc-autoSuggest__product-item'>
                        <a href={goToSearch(searchText)} className='msc-autoSuggest__product-link'>
                            {suggestion.ExtensionProperties?.find(prop => prop.Key === 'CustomerPartNumber')?.Value?.StringValue ||
                                suggestion.Value}
                        </a>
                    </li>
                ))}
            </Node>
        </Node>
    ) : (
        <></>
    );
};

function goToSearch(searchText: string) {
    // Get current base URL (protocol + host + first path segment)
    const { protocol, host, pathname } = window.location;

    // Example: https://obscommerce-nonprod.dynamics365commerce.ms/obscommerce/
    const basePath = pathname.split('/')[1] ? `/${pathname.split('/')[1]}` : '';
    const baseUrl = `${protocol}//${host}${basePath}/search`;

    // Extract domain from query params if available
    const params = new URLSearchParams(window.location.search);
    const domain = params.get('domain') || 'www.obriensteel.com'; // fallback

    const searchUrl = `${baseUrl}?domain=${domain}&q=${encodeURIComponent(searchText)}`;

    return searchUrl;
}
